import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditCard, ShieldCheck, ShieldAlert, Plus } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard, NeuInput } from "../../../components/neu/Neu";
import { paymentsHealth, registerPaymentDomain } from "../../../api/platform";

const Flag = ({ ok, label }) => (
  <span className={`flex items-center gap-1 text-xs font-bold ${ok ? "text-primary" : "text-destructive"}`}>
    {ok ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />} {label}
  </span>
);

// جاهزية الدفع: نوع المفاتيح، الحساب، الـ webhook، ودوميناتApple Pay / Google Pay.
export const PaymentsHealthCard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => paymentsHealth().then(setData).catch(() => setData({ error: "unavailable" }));
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    setBusy(true);
    try {
      await registerPaymentDomain(domain.trim().toLowerCase());
      toast.success(t("system.payDomainAdded"));
      setDomain("");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;

  return (
    <NeuCard data-testid="payments-health-card">
      <p className="label-xs flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-primary" /> {t("system.payHealth")}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{t("system.payHealthHint")}</p>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <Flag ok={data.mode === "live"} label={`${t("system.payMode")}: ${data.mode}`} />
        <Flag ok={!!data.webhook_secret_set} label={t("system.payWebhookSecret")} />
        <Flag ok={!!data.charges_enabled} label={t("system.payCharges")} />
        <Flag ok={!!data.payouts_enabled} label={t("system.payPayouts")} />
      </div>
      {data.error ? (
        <p className="mt-3 font-mono text-xs text-destructive" data-testid="payments-health-error">
          {data.error}
        </p>
      ) : null}

      <div className="mt-4 grid gap-2" data-testid="payments-domains">
        {(data.domains || []).map((row) => (
          <div key={row.domain} className="neu-in flex flex-wrap items-center gap-3 rounded-2xl px-4 py-2">
            <span className="font-mono text-xs font-bold">{row.domain}</span>
            {Object.entries(row.wallets || {}).map(([name, ok]) => (
              <Flag key={name} ok={ok} label={name} />
            ))}
          </div>
        ))}
        {(data.domains || []).length ? null : (
          <p className="text-xs text-muted-foreground">{t("system.payNoDomain")}</p>
        )}
      </div>

      <div className="mt-4 grid gap-2 sm:flex sm:items-center">
        <NeuInput
          dir="ltr"
          className="w-full font-mono text-sm sm:flex-1"
          placeholder="helcoo.uk"
          data-testid="payments-domain-input"
          value={domain}
          onChange={(event) => setDomain(event.target.value)}
        />
        <NeuButton
          variant="primary"
          className="flex shrink-0 items-center justify-center gap-2 whitespace-nowrap py-2.5 sm:px-6"
          data-testid="payments-domain-add"
          disabled={busy || domain.trim().length < 4}
          onClick={add}
        >
          <Plus className="h-4 w-4" /> {t("system.payAddDomain")}
        </NeuButton>
      </div>
      <p className="mt-2 text-[11px] leading-6 text-muted-foreground">{t("system.payWalletHint")}</p>
    </NeuCard>
  );
};
